/* eslint-disable camelcase */
import React, { Component } from 'react';
import ReactTable from 'react-table';
import { match as Match } from 'react-router-dom';
import { History } from 'history';
import PayrollSubComponent from './PayrollSubComponent';
import { TSelectedBookingSummary, TPayrollFreelancer } from './types';

import { TPageNotification, TApiMeta } from '../../../types';

// React-table cell type with data and value types
type TableCell<TData, TValue> = {
    value: TValue;
    original: TData;
};

// React-table row info type
type TableRowInfo<T = any> = {
    original: T;
    nestingPath: number[];
};

// React-table state type
type TableState = {
    expanded: Record<number, boolean>;
};

interface PayrollTableProps {
    expanded: Record<number, boolean>;
    match: Match;
    onRowExpand: (id: number) => void;
    onRowCollapse: (id: number) => void;
    loading: boolean;
    content: TPayrollFreelancer[];
    meta: TApiMeta;
    notification: TPageNotification;
    setTable: (table: ReactTable | null) => void;
    reloadData: () => void;
    onSortedChange: (newSorted: { id: string; desc: boolean }[], column: any, shiftKey: boolean) => void;
    onPageChange: (page: number) => void;
    page: number;
    errorMsg: string | undefined;
    history: History;
    setRef: (id: number, ref: any) => void;
    onSelectionChange?: (
        freelancerId: number,
        bookings: TSelectedBookingSummary[],
    ) => void;
    pendingDeselection: Record<number, number[]>;
    onDeselectionProcessed: (freelancerId: number, bookingIds: number[]) => void;
    generatedBookings: Record<number, number[]>;
}

export default class PayrollTable extends Component<PayrollTableProps> {
    tableRef: ReactTable | null = null;

    getTrProps = (state: TableState, rowInfo?: TableRowInfo<TPayrollFreelancer>) => ({
        onClick: () => {
            if (!rowInfo) {
                return;
            }
            const { expanded } = state;
            const { id } = rowInfo.original;
            const path = rowInfo.nestingPath[0];
            const diff = !expanded[path];
            if (diff) {
                this.props.onRowExpand(id);
            } else {
                this.props.onRowCollapse(id);
            }
        },
    });

    fireFetchData = () => {
        // Removed direct prop reload to avoid circular calls, let the parent handle it
        // this.props.reloadData();
    };

    setExpanded = (expanded: Record<number, boolean> | number): Record<number, boolean> => {
        if (typeof expanded === 'object' && expanded !== null) {
            return expanded;
        }
        if (typeof expanded === 'number' && expanded !== -1) {
            return { [expanded]: true };
        }
        return {};
    };

    render() {
        const columns = [
            {
                expander: true,
                Expander: ({ isExpanded }: { isExpanded: boolean }) => (
                    <div
                        className={`collapsible-table-expander ${
                            isExpanded ? 'active' : ''
                        }`}
                    >
                        <i className="icon icon-arrow-right-12" />
                    </div>
                ),
            },
            {
                Header: '',
                accessor: 'photo_thumbnail_medium',
                sortable: false,
                headerClassName: 'xsmall-col',
                className: 'xsmall-col',
                Cell: (row: TableCell<TPayrollFreelancer, string>) => {
                    const freelancerImg = row.value
                        ? row.value
                        : '/static/images/default_profile.png';
                    return (
                        <div
                            className="avatar"
                            style={{
                                backgroundImage: `url(${freelancerImg})`,
                                borderRadius: '50%',
                                backgroundSize: 'cover',
                                backgroundPosition: '50%',
                                height: '45px',
                                width: '45px',
                                marginTop: '2.5px',
                            }}
                        />
                    );
                },
            },
            {
                Header: 'Name',
                accessor: 'full_name',
                Cell: (row: TableCell<TPayrollFreelancer, string>) => (
                    <a
                        className="group-link"
                        href={`/operations/freelancer/${row.original.id}`}
                    >
                        {row.value}
                    </a>
                ),
            },
            {
                Header: 'Total Scheduled Hours',
                accessor: 'total_scheduled_hours',
            },
            {
                Header: 'Total Worked Hours',
                accessor: 'total_worked_hours',
                Cell: (row: TableCell<TPayrollFreelancer, number>) => {
                    const totalHours = row.value;
                    if (typeof totalHours !== 'number') {
                        return totalHours;
                    }
                    if (totalHours % 1 > 0.9) {
                        return Math.ceil(totalHours);
                    }
                    if (totalHours % 1 < 0.1) {
                        return Math.floor(totalHours);
                    }
                    return totalHours.toFixed(2);
                },
            },
            {
                Header: 'No. of Shifts',
                accessor: 'total_bookings',
            },
            {
                Header: 'Total Pay',
                accessor: 'total_pay',
                Cell: (row: TableCell<TPayrollFreelancer, number>) => <span>£{row.value?.toFixed(2)}</span>,
            },
        ];

        const { content } = this.props;
        let pages = -1;
        let showPaginationBottom = false;
        if (content && this.props.meta) {
            pages = Math.ceil(this.props.meta.count / 20);
            showPaginationBottom = this.props.meta.count > 20;
        }

        return (
            <ReactTable
                className="table-content"
                loading={this.props.loading}
                data={content || []}
                columns={columns}
                ref={(table: ReactTable | null) => {
                    this.tableRef = table;
                    this.props.setTable(table);
                }}
                SubComponent={(row: TableRowInfo<TPayrollFreelancer>) => (
                    <PayrollSubComponent
                        notification={this.props.notification}
                        table={this.tableRef}
                        match={this.props.match}
                        id={row.original.id}
                        worker={row.original}
                        onRowCollapse={this.props.onRowCollapse}
                        setRef={(ref: any) =>
                            this.props.setRef(row.original.id, ref)
                        }
                        location={this.props.history.location}
                        onSelectionChange={this.props.onSelectionChange}
                        pendingDeselection={
                            this.props.pendingDeselection[row.original.id]
                        }
                        onDeselectionProcessed={this.props.onDeselectionProcessed}
                        generatedBookings={
                            this.props.generatedBookings[row.original.id]
                        }
                    />
                )}
                getTrProps={this.getTrProps}
                pageSize={content ? content.length : 0}
                noDataText={this.props.errorMsg || 'No Workers Found'}
                showPaginationBottom={showPaginationBottom}
                showPageSizeOptions={false}
                page={this.props.page}
                pages={pages}
                expanded={this.setExpanded(this.props.expanded)}
                onSortedChange={this.props.onSortedChange}
                onPageChange={this.props.onPageChange}
                manual={true}
            />
        );
    }
}

